import { useQuery } from "@tanstack/react-query"
import { Button, useToast } from "@ternary/ui"
import type { ReactElement } from "react"
import { Link } from "react-router-dom"
import { loadAdminProducts } from "../api.js"
import { signupUrl } from "../product-draft.js"

/**
 * An empty state's next step while nobody has signed up: the first free product's signup link
 * with a Copy button, or a link to Products when there is no free product (yet).
 */
export function SignupLinkAction(): ReactElement {
  const { toast } = useToast()
  const products = useQuery({
    queryFn: ({ signal }) => loadAdminProducts({ signal }),
    queryKey: ["admin-products"],
  })

  async function copyLink(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      toast({ message: "Signup link copied." })
    } catch {
      toast({ message: `The link could not be copied: ${url}`, tone: "error" })
    }
  }

  if (products.isPending) {
    return (
      <Button disabled variant="secondary">
        Loading signup link…
      </Button>
    )
  }
  const free = products.data?.freeProducts[0]
  const shareUrl = free && products.data ? signupUrl(products.data.storefrontUrl, free.slug) : null
  if (!shareUrl) {
    return (
      <Link className="tny-button tny-button-secondary" to="/dashboard/products">
        Go to Products
      </Link>
    )
  }
  return (
    <>
      <a href={shareUrl} rel="noreferrer" target="_blank">
        {shareUrl.replace(/^https?:\/\//, "")}
      </a>
      <Button onClick={() => void copyLink(shareUrl)} variant="secondary">
        Copy signup link
      </Button>
    </>
  )
}
